import { createFileRoute, redirect } from '@tanstack/react-router';
import { createAuthTokenCookieFn } from '@/api/services/cluster/dashboard';
import { createEphemeralDashboardTokenFn } from '@/api/services/dashboard-token';
import { getDashboardUrl } from '@/components/Clusters/Metrics/utils';
import { reportMessage } from '@/helpers/errors/report';
import { safeAsync } from '@/helpers/errors/safeAsync';
import { head } from '@/router/Head';

export const Route = createFileRoute('/_authenticated/_dashboard/accounts/$accountId/clusters/$clusterId/dashboard')({
  staticData: {
    title: 'Cluster Dashboard',
  },
  beforeLoad: async ({ context: { queryClient, clusterQuery }, params: { accountId, clusterId } }) => {
    const { cluster } = await queryClient.ensureQueryData(clusterQuery);
    const url = URL.parse(getDashboardUrl(cluster));
    if (!url) {
      // Reporting should be implemented at the interceptor level, but for now we manually report the error to Sentry so we can track it.
      reportMessage('Failed to get the cluster dashboard URL', {
        domain: 'cluster',
        action: 'dashboard',
        level: 'fatal',
        visibility: 'error-page',
        extra: { accountId, clusterId },
      });
      throw new Error(`Failed to get the dashboard URL for cluster ${clusterId}`);
    }

    const [ephemeralDashboardTokenError, ephemeralDashboardTokenData] = await safeAsync(
      createEphemeralDashboardTokenFn({
        client: queryClient,
        accountId,
        clusterId,
      }),
    );
    if (ephemeralDashboardTokenError || !ephemeralDashboardTokenData?.ephemeralDashboardToken?.token) {
      // Reporting should be implemented at the interceptor level, but for now we manually report the error to Sentry so we can track it.
      reportMessage('Failed to grant authentication token for dashboard', {
        domain: 'cluster',
        action: 'dashboard',
        level: 'fatal',
        visibility: 'error-page',
        extra: { accountId, clusterId },
      });
      throw new Error(`Failed to grant authentication token for dashboard of cluster ${clusterId}`);
    }

    const { ephemeralDashboardToken } = ephemeralDashboardTokenData;
    const [_, authTokenCookieError] = await safeAsync(
      createAuthTokenCookieFn({
        body: { encrypted_cookie: ephemeralDashboardToken.token },
        baseUrl: new URL('_auth_token', url).toString(),
      }),
    );
    if (authTokenCookieError) {
      // Reporting should be implemented at the interceptor level, but for now we manually report the error to Sentry so we can track it.
      reportMessage('Failed to create auth token cookie for cluster dashboard', {
        domain: 'cluster',
        action: 'dashboard',
        level: 'error',
        visibility: 'silent',
        extra: { accountId, clusterId },
      });
    }

    return redirect({ href: url.toString(), replace: true });
  },
  head,
});
